// SPDX-License-Identifier: BUSL-1.1; Copyright (c) 2025 Social Connect Labs, Inc.; Licensed under BUSL-1.1 (see LICENSE); Apache-2.0 from 2029-06-11

// src/stores/protocolStore.ts
import { create } from "zustand";
import {
  API_URL,
  API_URL_STAGING,
  CSCA_TREE_URL,
  CSCA_TREE_URL_ID_CARD,
  CSCA_TREE_URL_STAGING,
  CSCA_TREE_URL_STAGING_ID_CARD,
  DSC_TREE_URL,
  DSC_TREE_URL_ID_CARD,
  DSC_TREE_URL_STAGING,
  DSC_TREE_URL_STAGING_ID_CARD,
  fetchOfacTrees,
  IDENTITY_TREE_URL,
  IDENTITY_TREE_URL_ID_CARD,
  IDENTITY_TREE_URL_STAGING,
  IDENTITY_TREE_URL_STAGING_ID_CARD,
  TREE_URL,
  TREE_URL_STAGING
} from "@selfxyz/common";
async function fetchWithTimeout(url, options, timeoutMs = 3e4) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error(`Request timeout after ${timeoutMs}ms`);
    }
    throw error;
  }
}
async function fetchAllTreesAndCircuits(selfClient, docCategory, environment, authorityKeyIdentifier) {
  await selfClient.getProtocolState()[docCategory].fetch_all(environment, authorityKeyIdentifier);
}
function getAltCSCAPublicKeys(selfClient, docCategory) {
  if (docCategory === "aadhaar" || docCategory === "kyc") {
    return selfClient.getProtocolState()[docCategory].public_keys;
  }
  return selfClient.getProtocolState()[docCategory].alternative_csca;
}
function getCommitmentTree(selfClient, documentCategory) {
  const protocolStore = selfClient.getProtocolState();
  return protocolStore[documentCategory].commitment_tree;
}
var useProtocolStore = create((set, get) => ({
  passport: {
    commitment_tree: null,
    dsc_tree: null,
    csca_tree: null,
    deployed_circuits: null,
    circuits_dns_mapping: null,
    alternative_csca: {},
    ofac_trees: null,
    fetch_all: async (environment, ski) => {
      await Promise.all([
        get().passport.fetch_deployed_circuits(environment),
        get().passport.fetch_circuits_dns_mapping(environment),
        get().passport.fetch_csca_tree(environment),
        get().passport.fetch_dsc_tree(environment),
        get().passport.fetch_identity_tree(environment),
        get().passport.fetch_ofac_trees(environment),
        get().passport.fetch_alternative_csca(environment, ski)
      ]);
    },
    fetch_alternative_csca: async (environment, ski) => {
      const url = `${environment === "prod" ? API_URL : API_URL_STAGING}/ski-pems/${ski.toLowerCase()}`;
      try {
        const response = await fetch(url, {
          method: "GET"
        });
        if (!response.ok) {
          throw new Error(`HTTP error fetching ${url}! status: ${response.status}`);
        }
        const responseText = await response.text();
        const data = JSON.parse(responseText);
        set({ passport: { ...get().passport, alternative_csca: data.data } });
      } catch (error) {
        console.error(`Failed fetching alternative CSCA from ${url}:`, error);
        set({ passport: { ...get().passport, alternative_csca: {} } });
      }
    },
    fetch_deployed_circuits: async (environment) => {
      const url = `${environment === "prod" ? API_URL : API_URL_STAGING}/deployed-circuits`;
      try {
        const response = await fetchWithTimeout(url);
        if (!response.ok) {
          throw new Error(`HTTP error fetching ${url}! status: ${response.status}`);
        }
        const responseText = await response.text();
        const data = JSON.parse(responseText);
        set({ passport: { ...get().passport, deployed_circuits: data.data } });
      } catch (error) {
        console.error(`Failed fetching deployed circuits from ${url}:`, error);
        set({ passport: { ...get().passport, deployed_circuits: null } });
      }
    },
    fetch_circuits_dns_mapping: async (environment) => {
      const url = `${environment === "prod" ? API_URL : API_URL_STAGING}/circuit-dns-mapping-gcp`;
      try {
        const response = await fetchWithTimeout(url);
        if (!response.ok) {
          throw new Error(`HTTP error fetching ${url}! status: ${response.status}`);
        }
        const responseText = await response.text();
        const data = JSON.parse(responseText);
        set({
          passport: { ...get().passport, circuits_dns_mapping: data.data }
        });
      } catch (error) {
        console.error(`Failed fetching circuit DNS mapping from ${url}:`, error);
        set({ passport: { ...get().passport, circuits_dns_mapping: null } });
      }
    },
    fetch_csca_tree: async (environment) => {
      const url = environment === "prod" ? CSCA_TREE_URL : CSCA_TREE_URL_STAGING;
      try {
        const response = await fetchWithTimeout(url);
        if (!response.ok) {
          throw new Error(`HTTP error fetching ${url}! status: ${response.status}`);
        }
        const responseText = await response.text();
        const rawData = JSON.parse(responseText);
        let treeData;
        if (rawData && rawData.data) {
          treeData = typeof rawData.data === "string" ? JSON.parse(rawData.data) : rawData.data;
        } else {
          treeData = rawData;
        }
        set({ passport: { ...get().passport, csca_tree: treeData } });
      } catch (error) {
        console.error(`Failed fetching CSCA tree from ${url}:`, error);
        set({ passport: { ...get().passport, csca_tree: null } });
      }
    },
    fetch_dsc_tree: async (environment) => {
      const url = environment === "prod" ? DSC_TREE_URL : DSC_TREE_URL_STAGING;
      try {
        const response = await fetchWithTimeout(url);
        if (!response.ok) {
          throw new Error(`HTTP error fetching ${url}! status: ${response.status}`);
        }
        const responseText = await response.text();
        const data = JSON.parse(responseText);
        set({ passport: { ...get().passport, dsc_tree: data.data } });
      } catch (error) {
        console.error(`Failed fetching DSC tree from ${url}:`, error);
        set({ passport: { ...get().passport, dsc_tree: null } });
      }
    },
    fetch_identity_tree: async (environment) => {
      const url = environment === "prod" ? IDENTITY_TREE_URL : IDENTITY_TREE_URL_STAGING;
      try {
        const response = await fetchWithTimeout(url);
        if (!response.ok) {
          throw new Error(`HTTP error fetching ${url}! status: ${response.status}`);
        }
        const responseText = await response.text();
        const data = JSON.parse(responseText);
        set({ passport: { ...get().passport, commitment_tree: data.data } });
      } catch (error) {
        console.error(`Failed fetching identity tree from ${url}:`, error);
        set({ passport: { ...get().passport, commitment_tree: null } });
      }
    },
    fetch_ofac_trees: async (environment) => {
      try {
        const trees = await fetchOfacTrees(environment, "passport");
        set({ passport: { ...get().passport, ofac_trees: trees } });
      } catch (error) {
        console.error("Failed fetching OFAC trees:", error);
        set({ passport: { ...get().passport, ofac_trees: null } });
      }
    }
  },
  id_card: {
    commitment_tree: null,
    dsc_tree: null,
    csca_tree: null,
    deployed_circuits: null,
    circuits_dns_mapping: null,
    alternative_csca: {},
    ofac_trees: null,
    fetch_all: async (environment, ski) => {
      await Promise.all([
        get().id_card.fetch_deployed_circuits(environment),
        get().id_card.fetch_circuits_dns_mapping(environment),
        get().id_card.fetch_csca_tree(environment),
        get().id_card.fetch_dsc_tree(environment),
        get().id_card.fetch_identity_tree(environment),
        get().id_card.fetch_ofac_trees(environment),
        get().id_card.fetch_alternative_csca(environment, ski)
      ]);
    },
    fetch_deployed_circuits: async (environment) => {
      const url = `${environment === "prod" ? API_URL : API_URL_STAGING}/deployed-circuits`;
      try {
        const response = await fetchWithTimeout(url);
        if (!response.ok) {
          throw new Error(`HTTP error fetching ${url}! status: ${response.status}`);
        }
        const responseText = await response.text();
        const data = JSON.parse(responseText);
        set({ id_card: { ...get().id_card, deployed_circuits: data.data } });
      } catch (error) {
        console.error(`Failed fetching deployed circuits from ${url}:`, error);
        set({ id_card: { ...get().id_card, deployed_circuits: null } });
      }
    },
    fetch_circuits_dns_mapping: async (environment) => {
      const url = `${environment === "prod" ? API_URL : API_URL_STAGING}/circuit-dns-mapping-gcp`;
      try {
        const response = await fetchWithTimeout(url);
        if (!response.ok) {
          throw new Error(`HTTP error fetching ${url}! status: ${response.status}`);
        }
        const responseText = await response.text();
        const data = JSON.parse(responseText);
        set({
          id_card: { ...get().id_card, circuits_dns_mapping: data.data }
        });
      } catch (error) {
        console.error(`Failed fetching circuit DNS mapping from ${url}:`, error);
        set({ id_card: { ...get().id_card, circuits_dns_mapping: null } });
      }
    },
    fetch_csca_tree: async (environment) => {
      const url = environment === "prod" ? CSCA_TREE_URL_ID_CARD : CSCA_TREE_URL_STAGING_ID_CARD;
      try {
        const response = await fetchWithTimeout(url);
        if (!response.ok) {
          throw new Error(`HTTP error fetching ${url}! status: ${response.status}`);
        }
        const responseText = await response.text();
        const rawData = JSON.parse(responseText);
        let treeData;
        if (rawData && rawData.data) {
          treeData = typeof rawData.data === "string" ? JSON.parse(rawData.data) : rawData.data;
        } else {
          treeData = rawData;
        }
        set({ id_card: { ...get().id_card, csca_tree: treeData } });
      } catch (error) {
        console.error(`Failed fetching CSCA tree from ${url}:`, error);
        set({ id_card: { ...get().id_card, csca_tree: null } });
      }
    },
    fetch_dsc_tree: async (environment) => {
      const url = environment === "prod" ? DSC_TREE_URL_ID_CARD : DSC_TREE_URL_STAGING_ID_CARD;
      try {
        const response = await fetchWithTimeout(url);
        if (!response.ok) {
          throw new Error(`HTTP error fetching ${url}! status: ${response.status}`);
        }
        const responseText = await response.text();
        const data = JSON.parse(responseText);
        set({ id_card: { ...get().id_card, dsc_tree: data.data } });
      } catch (error) {
        console.error(`Failed fetching DSC tree from ${url}:`, error);
        set({ id_card: { ...get().id_card, dsc_tree: null } });
      }
    },
    fetch_identity_tree: async (environment) => {
      const url = environment === "prod" ? IDENTITY_TREE_URL_ID_CARD : IDENTITY_TREE_URL_STAGING_ID_CARD;
      try {
        const response = await fetchWithTimeout(url);
        if (!response.ok) {
          throw new Error(`HTTP error fetching ${url}! status: ${response.status}`);
        }
        const responseText = await response.text();
        const data = JSON.parse(responseText);
        set({ id_card: { ...get().id_card, commitment_tree: data.data } });
      } catch (error) {
        console.error(`Failed fetching identity tree from ${url}:`, error);
        set({ id_card: { ...get().id_card, commitment_tree: null } });
      }
    },
    fetch_alternative_csca: async (environment, ski) => {
      const url = `${environment === "prod" ? API_URL : API_URL_STAGING}/ski-pems/${ski.toLowerCase()}`;
      try {
        const response = await fetchWithTimeout(url, {
          method: "GET"
        });
        if (!response.ok) {
          throw new Error(`HTTP error fetching ${url}! status: ${response.status}`);
        }
        const responseText = await response.text();
        const data = JSON.parse(responseText);
        set({ id_card: { ...get().id_card, alternative_csca: data.data } });
      } catch (error) {
        console.error(`Failed fetching alternative CSCA from ${url}:`, error);
        set({ id_card: { ...get().id_card, alternative_csca: {} } });
      }
    },
    fetch_ofac_trees: async (environment) => {
      try {
        const trees = await fetchOfacTrees(environment, "id_card");
        set({ id_card: { ...get().id_card, ofac_trees: trees } });
      } catch (error) {
        console.error("Failed fetching OFAC trees:", error);
        set({ id_card: { ...get().id_card, ofac_trees: null } });
      }
    }
  },
  aadhaar: {
    commitment_tree: null,
    public_keys: null,
    deployed_circuits: null,
    circuits_dns_mapping: null,
    ofac_trees: null,
    fetch_all: async (environment) => {
      try {
        await Promise.all([
          get().aadhaar.fetch_deployed_circuits(environment),
          get().aadhaar.fetch_circuits_dns_mapping(environment),
          get().aadhaar.fetch_public_keys(environment),
          get().aadhaar.fetch_identity_tree(environment),
          get().aadhaar.fetch_ofac_trees(environment)
        ]);
      } catch (error) {
        console.error(`Failed fetching Aadhaar data for ${environment}:`, error);
        throw error;
      }
    },
    fetch_deployed_circuits: async (environment) => {
      const url = `${environment === "prod" ? API_URL : API_URL_STAGING}/deployed-circuits`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error fetching ${url}! status: ${response.status}`);
      }
      const responseText = await response.text();
      const data = JSON.parse(responseText);
      set({ aadhaar: { ...get().aadhaar, deployed_circuits: data.data } });
    },
    fetch_circuits_dns_mapping: async (environment) => {
      const url = `${environment === "prod" ? API_URL : API_URL_STAGING}/circuit-dns-mapping-gcp`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error fetching ${url}! status: ${response.status}`);
      }
      const responseText = await response.text();
      const data = JSON.parse(responseText);
      set({
        aadhaar: { ...get().aadhaar, circuits_dns_mapping: data.data }
      });
    },
    fetch_public_keys: async (environment) => {
      const url = environment === "prod" ? `${TREE_URL}/aadhaar-pubkeys` : `${TREE_URL_STAGING}/aadhaar-pubkeys`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error fetching ${url}! status: ${response.status}`);
      }
      const responseText = await response.text();
      const data = JSON.parse(responseText);
      set({ aadhaar: { ...get().aadhaar, public_keys: data.data } });
    },
    fetch_identity_tree: async (environment) => {
      const url = `${environment === "prod" ? TREE_URL : TREE_URL_STAGING}/identity-aadhaar`;
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error fetching ${url}! status: ${response.status}`);
        }
        const responseText = await response.text();
        const data = JSON.parse(responseText);
        set({ aadhaar: { ...get().aadhaar, commitment_tree: data.data } });
      } catch (error) {
        console.error(`Failed fetching Aadhaar identity tree from ${url}:`, error);
      }
    },
    fetch_ofac_trees: async (environment) => {
      const baseUrl = environment === "prod" ? TREE_URL : TREE_URL_STAGING;
      const nameDobUrl = `${baseUrl}/ofac/name-dob-aadhaar`;
      const nameYobUrl = `${baseUrl}/ofac/name-yob-aadhaar`;
      try {
        const fetchTree = async (url) => {
          const res = await fetch(url);
          if (!res.ok) {
            throw new Error(`HTTP error fetching ${url}! status: ${res.status}`);
          }
          const responseData = await res.json();
          if (responseData && typeof responseData === "object" && "status" in responseData) {
            if (responseData.status !== "success" || !responseData.data) {
              throw new Error(`Failed to fetch tree from ${url}: ${responseData.message || "Invalid response format"}`);
            }
            return responseData.data;
          }
          return responseData;
        };
        const [nameDobData, nameYobData] = await Promise.all([fetchTree(nameDobUrl), fetchTree(nameYobUrl)]);
        set({
          aadhaar: {
            ...get().aadhaar,
            ofac_trees: {
              passportNoAndNationality: null,
              nameAndDob: nameDobData,
              nameAndYob: nameYobData
            }
          }
        });
      } catch (error) {
        console.error("Failed fetching Aadhaar OFAC trees:", error);
        set({ aadhaar: { ...get().aadhaar, ofac_trees: null } });
      }
    }
  },
  //add empty functions for kyc
  kyc: {
    commitment_tree: null,
    public_keys: null,
    deployed_circuits: null,
    circuits_dns_mapping: null,
    ofac_trees: null,
    fetch_all: async (environment) => {
      try {
        await Promise.all([
          get().kyc.fetch_deployed_circuits(environment),
          get().kyc.fetch_circuits_dns_mapping(environment),
          get().kyc.fetch_public_keys(environment),
          get().kyc.fetch_identity_tree(environment),
          get().kyc.fetch_ofac_trees(environment)
        ]);
      } catch (error) {
        console.error(`Failed fetching kyc data for ${environment}:`, error);
        throw error;
      }
    },
    fetch_deployed_circuits: async (environment) => {
      const url = `${environment === "prod" ? API_URL : API_URL_STAGING}/deployed-circuits`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error fetching ${url}! status: ${response.status}`);
      }
      const responseText = await response.text();
      const data = JSON.parse(responseText);
      set({ kyc: { ...get().kyc, deployed_circuits: data.data } });
    },
    fetch_circuits_dns_mapping: async (environment) => {
      const url = `${environment === "prod" ? API_URL : API_URL_STAGING}/circuit-dns-mapping-gcp`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error fetching ${url}! status: ${response.status}`);
      }
      const responseText = await response.text();
      const data = JSON.parse(responseText);
      set({
        kyc: { ...get().kyc, circuits_dns_mapping: data.data }
      });
    },
    fetch_public_keys: async (_environment) => {
      set({ kyc: { ...get().kyc, public_keys: null } });
    },
    fetch_identity_tree: async (environment) => {
      const url = `${environment === "prod" ? TREE_URL : TREE_URL_STAGING}/identity-kyc`;
      try {
        const response = await fetchWithTimeout(url);
        if (!response.ok) {
          throw new Error(`HTTP error fetching ${url}! status: ${response.status}`);
        }
        const responseText = await response.text();
        const data = JSON.parse(responseText);
        set({ kyc: { ...get().kyc, commitment_tree: data.data } });
      } catch (error) {
        console.error(`Failed fetching kyc identity tree from ${url}:`, error);
        set({ kyc: { ...get().kyc, commitment_tree: null } });
      }
    },
    fetch_ofac_trees: async (environment) => {
      const baseUrl = environment === "prod" ? TREE_URL : TREE_URL_STAGING;
      const nameDobUrl = `${baseUrl}/ofac/name-dob-kyc`;
      const nameYobUrl = `${baseUrl}/ofac/name-yob-kyc`;
      try {
        const fetchTree = async (url) => {
          const res = await fetch(url);
          if (!res.ok) {
            throw new Error(`HTTP error fetching ${url}! status: ${res.status}`);
          }
          const responseData = await res.json();
          if (responseData && typeof responseData === "object" && "status" in responseData) {
            if (responseData.status !== "success" || !responseData.data) {
              throw new Error(`Failed to fetch tree from ${url}: ${responseData.message || "Invalid response format"}`);
            }
            return responseData.data;
          }
          return responseData;
        };
        const [nameDobData, nameYobData] = await Promise.all([fetchTree(nameDobUrl), fetchTree(nameYobUrl)]);
        set({
          kyc: {
            ...get().kyc,
            ofac_trees: {
              passportNoAndNationality: null,
              nameAndDob: nameDobData,
              nameAndYob: nameYobData
            }
          }
        });
      } catch (error) {
        console.error("Failed fetching kyc OFAC trees:", error);
        set({ kyc: { ...get().kyc, ofac_trees: null } });
      }
    }
  }
}));

export {
  fetchAllTreesAndCircuits,
  getAltCSCAPublicKeys,
  getCommitmentTree,
  useProtocolStore
};
//# sourceMappingURL=chunk-OTRLGZMA.js.map